import React from 'react';
import type { Editor } from '@tiptap/react';

type RichTextImageLightboxParams = {
  editor: Editor | null;
  hostRef: React.RefObject<HTMLDivElement | null>;
};

type LightboxSlide = {
  src: string;
  alt?: string;
};

const resolveClickedImage = (
  event: MouseEvent,
  editorRoot: HTMLElement
): HTMLImageElement | null => {
  const pathImage = (event.composedPath?.() || []).find(
    (node): node is HTMLImageElement =>
      node instanceof HTMLImageElement && editorRoot.contains(node)
  );

  if (pathImage) return pathImage;

  const fallbackImage = (event.target as Element | null)?.closest?.('img');
  return fallbackImage instanceof HTMLImageElement && editorRoot.contains(fallbackImage)
    ? fallbackImage
    : null;
};

type RichTextImageLightboxResult = {
  lightboxSlides: LightboxSlide[];
  lightboxIndex: number;
  isLightboxOpen: boolean;
  closeLightbox: () => void;
};

export const useRichTextImageLightbox = ({
  editor,
  hostRef,
}: RichTextImageLightboxParams): RichTextImageLightboxResult => {
  const [selectedImageIndex, setSelectedImageIndex] = React.useState<number | null>(null);
  const [lightboxSlides, setLightboxSlides] = React.useState<LightboxSlide[]>([]);
  const [lightboxIndex, setLightboxIndex] = React.useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = React.useState(false);

  React.useEffect(() => {
    if (!editor) return;

    const editorRoot = editor.view.dom as HTMLElement;
    const images = Array.from(editorRoot.querySelectorAll('img')).filter(
      (img): img is HTMLImageElement => Boolean(img.getAttribute('src'))
    );

    images.forEach((img, index) => {
      img.classList.toggle('rich-text-image-selected', selectedImageIndex === index);
    });
  }, [editor, selectedImageIndex]);

  React.useEffect(() => {
    if (!editor || !hostRef.current) return;

    const host = hostRef.current;
    const editorRoot = editor.view.dom as HTMLElement;

    const getImages = () => Array.from(editorRoot.querySelectorAll('img')) as HTMLImageElement[];

    const handleClick = (ev: MouseEvent) => {
      const img = resolveClickedImage(ev, editorRoot);
      if (!img) return;

      const images = getImages();
      const slides = images
        .filter((candidate) => !!candidate.getAttribute('src'))
        .map((candidate) => ({
          src: candidate.getAttribute('src') as string,
          alt: candidate.getAttribute('alt') || '',
        }));

      const index = slides.findIndex(
        (candidate) => candidate.src === (img?.getAttribute('src') || '')
      );
      if (index < 0) return;

      setLightboxSlides(slides);

      const anchor = img.closest('a') as HTMLAnchorElement | null;
      if (anchor) ev.preventDefault();

      if (selectedImageIndex === index) {
        setLightboxIndex(index);
        setIsLightboxOpen(true);
        return;
      }

      setSelectedImageIndex(index);
      ev.preventDefault();
    };

    host.addEventListener('click', handleClick, true);

    return () => {
      host.removeEventListener('click', handleClick, true);
    };
  }, [editor, hostRef, selectedImageIndex]);

  React.useEffect(() => {
    if (isLightboxOpen) return;

    setSelectedImageIndex(null);
    if (!editor) return;

    const editorRoot = editor.view.dom as HTMLElement;
    const images = Array.from(editorRoot.querySelectorAll('img')).filter(
      (img): img is HTMLImageElement => Boolean(img.getAttribute('src'))
    );
    images.forEach((img) => {
      img.classList.remove('rich-text-image-selected');
    });
  }, [editor, isLightboxOpen]);

  return {
    lightboxSlides,
    lightboxIndex,
    isLightboxOpen,
    closeLightbox: () => setIsLightboxOpen(false),
  };
};
